import { z } from 'zod';

// Mirrors `crm.crm_log_entry_type_enum` — MUST stay value-for-value identical
// to `crmLogEntryTypeEnumInCrm` in packages/db/src/schema/crm.ts (guarded by
// `apps/crm/api/src/crm-log/crm-log-enum-parity.test.ts`).
//
// This list is used to PARSE rows on the way out (`crmLog.list` runs the row
// through `crmLogSchema`), so a value the DB can store but this enum omits is
// not a cosmetic gap: the whole query 500s with `invalid_value` as soon as one
// such row lands on the page. That is exactly what happened to the three
// `company_attachment_*` values — `companies.service.ts` has been writing them
// since the supplier-attachment work, while this enum still listed only ten
// values. Adding a value to the pg enum without adding it here is therefore a
// runtime break, not a type error.
//
// The legacy backend only had `rating_change`; the contact/company and
// tour-operator lifecycle values come from the
// `2026-07-16-crm-log-entry-type-lifecycle-values` migration, the attachment
// values from `2026-08-04-crm-supplier-record`.
export const crmLogEntryTypeValues = [
  'rating_change',
  'contact_created',
  'contact_updated',
  'contact_deleted',
  'company_created',
  'company_updated',
  'company_deleted',
  'tour_operator_created',
  'tour_operator_updated',
  'tour_operator_deleted',
  // Supplier attachment actions (crm.company.attachments version chain).
  // `categorised` / `renamed` / `featured` deliberately reuse `replaced`.
  'company_attachment_added',
  'company_attachment_replaced',
  'company_attachment_removed',
  // Duplicate merge queue (XT-724), from `2026-09-18-xt724-crm-log-merge-*`.
  'company_merged',
  'contact_merged',
  'company_kept_separate',
] as const;
export const crmLogEntryTypeSchema = z.enum(crmLogEntryTypeValues);
export type CrmLogEntryType = z.infer<typeof crmLogEntryTypeSchema>;

// Verbatim from legacy
// `travel_planner_back/src/crm-log/enums/crm-log-target-entry-type.ts`.
export const crmLogTargetEntryTypeValues = [
  'company',
  'contact',
  'tour_operator',
] as const;
export const crmLogTargetEntryTypeSchema = z.enum(crmLogTargetEntryTypeValues);
export type CrmLogTargetEntryType = z.infer<typeof crmLogTargetEntryTypeSchema>;

// JSONB columns hold arbitrary record diffs — keep them as record(string, unknown).
const crmLogJsonValueSchema = z.record(z.string(), z.unknown()).nullable();

export const crmLogSchema = z.object({
  id: z.uuid(),
  createdOn: z.string(),
  accountId: z.uuid(),
  targetEntity: crmLogTargetEntryTypeSchema,
  targetId: z.uuid(),
  entryType: crmLogEntryTypeSchema,
  oldValue: crmLogJsonValueSchema,
  newValue: crmLogJsonValueSchema,
  comment: z.string().nullable(),
});
export type CrmLog = z.infer<typeof crmLogSchema>;

export const crmLogRowSchema = crmLogSchema
  .pick({
    id: true,
    createdOn: true,
    targetEntity: true,
    entryType: true,
    oldValue: true,
    newValue: true,
    comment: true,
  })
  .extend({
    createdBy: z.string(),
    targetName: z.string().nullable(),
  });
export type CrmLogRow = z.infer<typeof crmLogRowSchema>;

export const findCrmLogInput = z.object({
  limit: z.number().int().positive().max(100).default(10),
  offset: z.number().int().min(0).default(0),
  entryType: crmLogEntryTypeSchema.optional(),
});
export type FindCrmLogInput = z.infer<typeof findCrmLogInput>;

export const paginatedCrmLogSchema = z.object({
  data: z.array(crmLogRowSchema),
  count: z.number().int().min(0),
  countAll: z.number().int().min(0),
});
export type PaginatedCrmLog = z.infer<typeof paginatedCrmLogSchema>;

/** Entry types whose row describes a duplicate merge (XT-724). */
export const crmLogMergedEntryTypes = [
  'company_merged',
  'contact_merged',
] as const satisfies readonly CrmLogEntryType[];

export const crmLogMergeDetailInput = z.object({ id: z.uuid() });
export type CrmLogMergeDetailInput = z.infer<typeof crmLogMergeDetailInput>;

/** One record a merge moved from a retired record onto the one it kept. */
export const crmLogMovedRowSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  /** False when the record was deleted after the merge — nothing to open. */
  exists: z.boolean(),
  /** The retired record it belonged to; null when the log did not record it. */
  fromId: z.uuid().nullable(),
  /** The itinerary to open for it (itineraries, their items, payment logs). */
  itineraryId: z.uuid().nullable(),
  /** `Driver` | `Guide` for tour operators, which live on separate pages. */
  operatorType: z.string().nullable(),
});
export type CrmLogMovedRow = z.infer<typeof crmLogMovedRowSchema>;

export const crmLogMergeDetailSchema = z.object({
  entity: z.enum(['company', 'contact']),
  kept: z.object({ id: z.uuid(), name: z.string().nullable() }),
  retired: z.array(z.object({ id: z.uuid(), name: z.string().nullable() })),
  /** Fields the kept record was empty on and took from a retired one. */
  filledFields: z.array(z.string()),
  /** HubSpot Record IDs of the retired records, kept on the survivor. */
  mergedExternalIds: z.array(z.string()),
  moved: z.object({
    itineraries: z.array(crmLogMovedRowSchema),
    contacts: z.array(crmLogMovedRowSchema),
    paymentLogs: z.array(crmLogMovedRowSchema),
    itineraryItems: z.array(crmLogMovedRowSchema),
    inboxThreads: z.array(crmLogMovedRowSchema),
    tourOperators: z.array(crmLogMovedRowSchema),
    products: z.array(crmLogMovedRowSchema),
  }),
});
export type CrmLogMergeDetail = z.infer<typeof crmLogMergeDetailSchema>;
